import {
	INFRACTION_DEADLINE_SITUATION,
	INFRACTION_DEADLINE_TYPE,
} from "../constants/infraction-api-enums.constant";
import type { DeadlineUrgency } from "../types/infraction.types";
import type { InfractionListItemApiResponse } from "../types/infraction-api.types";
import { isDeadlineFallback } from "./infraction-list-display";

export type DeadlineStatusIconKind =
	| "clock"
	| "alert-circle"
	| "alert-triangle"
	| "check-circle"
	| "credit-card"
	| "file-question"
	| "x-circle"
	| "none";

const UPCOMING_DRIVER_INDICATION_CLOCK_THRESHOLD_DAYS = 7;

export function getDeadlineStatusIconKind(
	item: InfractionListItemApiResponse
): DeadlineStatusIconKind {
	const deadline = item.deadline;
	if (!deadline || deadline.type === INFRACTION_DEADLINE_TYPE.NONE) {
		if (isDeadlineFallback(item)) {
			return "file-question";
		}

		return "none";
	}

	if (deadline.type === INFRACTION_DEADLINE_TYPE.PAID) {
		return "credit-card";
	}

	if (deadline.type === INFRACTION_DEADLINE_TYPE.CANCELLED) {
		return "x-circle";
	}

	switch (deadline.situation) {
		case INFRACTION_DEADLINE_SITUATION.EXPIRED:
			return "alert-triangle";
		case INFRACTION_DEADLINE_SITUATION.DUE_TODAY:
			return "alert-circle";
		case INFRACTION_DEADLINE_SITUATION.UPCOMING: {
			if (deadline.type === INFRACTION_DEADLINE_TYPE.PAYMENT) {
				return "check-circle";
			}

			if (
				deadline.daysFromToday !== null &&
				deadline.daysFromToday > UPCOMING_DRIVER_INDICATION_CLOCK_THRESHOLD_DAYS
			) {
				return "clock";
			}

			return "alert-circle";
		}
		case INFRACTION_DEADLINE_SITUATION.COMPLETED:
			return "credit-card";
		case INFRACTION_DEADLINE_SITUATION.CANCELLED:
			return "x-circle";
		default:
			return "clock";
	}
}

export function getDeadlineStatusIconClassName(
	kind: DeadlineStatusIconKind
): string {
	switch (kind) {
		case "alert-triangle":
			return "text-fg-error-primary";
		case "alert-circle":
			return "text-fg-warning-primary";
		case "check-circle":
			return "text-fg-success-primary";
		case "credit-card":
			return "text-fg-brand-primary";
		case "x-circle":
			return "text-fg-warning-primary";
		case "clock":
		case "file-question":
			return "text-fg-quaternary";
		default:
			return "text-fg-quaternary";
	}
}

export function getDeadlineUrgencyClassName(urgency: DeadlineUrgency): string {
	switch (urgency) {
		case "critical":
			return "text-error-primary font-medium";
		case "warning":
			return "text-warning-primary font-medium";
		case "expired":
			return "text-error-primary line-through";
		case "paid":
			return "text-success-primary";
		case "cancelled":
			return "text-muted-foreground line-through";
		default:
			return "text-foreground";
	}
}
